import {
  collection,
  doc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  writeBatch,
  type Unsubscribe,
} from 'firebase/firestore'
import { firestoreDb } from '../firebase'
import {
  mutateAllUserNotifications,
  mutateUserNotifications,
} from '../notificationService'
import { isNoSignInPreviewActive } from '../../debug'
import { setHelperUnreadNotifications } from './helperStore'
import { requestAppDataRefresh } from '../../utils/appDataRefresh'

export type HelperNotificationDeeplinkKind = 'CHECKLIST' | 'MEETING' | 'CHAT' | 'WORKSPACE' | 'INVITE' | 'UNKNOWN'

export interface HelperChatNotificationTarget {
  channelId: string | null
  messageId: string | null
}

export interface HelperNotificationItem {
  id: string
  title: string
  body: string
  type: string
  eventType?: string | null
  workspaceId: string | null
  workspaceName?: string | null
  deeplink: { kind: HelperNotificationDeeplinkKind; refId: string | null } | null
  chatTarget: HelperChatNotificationTarget | null
  periodKey?: string | null
  readAt: number | null
  dismissedAt: number | null
  createdAt: number
}

const HARD_CAP = 200

const millis = (value: unknown): number | null => {
  if (!value) return null
  if (typeof value === 'object' && 'toMillis' in (value as Record<string, unknown>)) {
    try {
      return (value as { toMillis: () => number }).toMillis()
    } catch {
      return null
    }
  }
  if (typeof value === 'number') return value
  return null
}

const inferDeeplinkKind = (entityType: unknown, eventType: unknown): HelperNotificationDeeplinkKind => {
  if (typeof entityType === 'string') {
    if (entityType === 'CHECKLIST' || entityType === 'CHECKLIST_ITEM') return 'CHECKLIST'
    if (entityType === 'MEETING') return 'MEETING'
    if (entityType === 'CHAT' || entityType === 'CHANNEL' || entityType === 'MESSAGE') return 'CHAT'
    if (entityType === 'WORKSPACE') return 'WORKSPACE'
    if (entityType === 'INVITE') return 'INVITE'
  }
  if (typeof eventType === 'string' && eventType === 'INVITE_RECEIVED') return 'INVITE'
  if (typeof eventType === 'string' && eventType.startsWith('CHAT')) return 'CHAT'
  if (typeof eventType === 'string' && eventType.startsWith('MEETING')) return 'MEETING'
  return 'UNKNOWN'
}

const normalizeChatTarget = (
  entityType: unknown,
  entityId: unknown,
  payload: unknown,
): HelperChatNotificationTarget | null => {
  const data = payload && typeof payload === 'object'
    ? payload as Record<string, unknown>
    : null
  const channelIdFromPayload = typeof data?.channelId === 'string' ? data.channelId : null
  const messageIdFromPayload = typeof data?.messageId === 'string' ? data.messageId : null
  const entityIdString = typeof entityId === 'string' ? entityId : null

  const channelId = channelIdFromPayload
    ?? (entityType === 'CHANNEL' ? entityIdString : null)
  const messageId = messageIdFromPayload
    ?? (entityType === 'MESSAGE' ? entityIdString : null)

  if (!channelId && !messageId) return null
  return { channelId, messageId }
}

export const projectHelperNotificationItem = (id: string, data: Record<string, unknown>): HelperNotificationItem => {
  const created = millis(data.createdAt) ?? 0
  const kind = inferDeeplinkKind(data.entityType, data.eventType)
  return {
    id,
    title: typeof data.title === 'string' ? data.title : 'Notification',
    body: typeof data.body === 'string' ? data.body : '',
    type: typeof data.category === 'string' ? data.category : (typeof data.eventType === 'string' ? data.eventType : 'GENERIC'),
    eventType: typeof data.eventType === 'string' ? data.eventType : null,
    workspaceId: typeof data.workspaceId === 'string' ? data.workspaceId : null,
    workspaceName: typeof data.workspaceName === 'string' ? data.workspaceName : null,
    deeplink: {
      kind,
      refId: typeof data.entityId === 'string' ? data.entityId : null,
    },
    chatTarget: kind === 'CHAT'
      ? normalizeChatTarget(data.entityType, data.entityId, data.data)
      : null,
    periodKey: typeof (data.data as Record<string, unknown> | undefined)?.periodKey === 'string'
      ? ((data.data as Record<string, unknown>).periodKey as string)
      : null,
    readAt: millis(data.readAt),
    dismissedAt: millis(data.dismissedAt) ?? millis(data.clearedAt),
    createdAt: created,
  }
}

export const subscribeToHelperNotifications = (
  uid: string,
  onChange: (items: HelperNotificationItem[]) => void,
  opts?: { hide?: (item: HelperNotificationItem) => boolean },
): Unsubscribe => {
  if (isNoSignInPreviewActive()) {
    setHelperUnreadNotifications(0)
    onChange([])
    return () => {}
  }
  const q = query(
    collection(firestoreDb, 'notifications', uid, 'items'),
    orderBy('createdAt', 'desc'),
    limit(HARD_CAP),
  )
  let initialized = false
  return onSnapshot(
    q,
    (snapshot) => {
      if (initialized && snapshot.docChanges().some((change) => change.type === 'added')) {
        requestAppDataRefresh('helper-notification')
      }
      const rawItems = snapshot.docs
        .map((entry) => projectHelperNotificationItem(entry.id, entry.data() as Record<string, unknown>))
        .filter((item) => !item.dismissedAt)
      const items = opts?.hide ? rawItems.filter((item) => !opts.hide!(item)) : rawItems
      const unread = items.filter((item) => !item.readAt).length
      setHelperUnreadNotifications(unread)
      onChange(items)
      initialized = true
    },
    () => {
      setHelperUnreadNotifications(0)
      onChange([])
    },
  )
}

export const markHelperNotificationRead = async (uid: string, id: string): Promise<void> => {
  await mutateUserNotifications('READ', [id]).catch(async () => {
    await updateDoc(doc(firestoreDb, 'notifications', uid, 'items', id), {
      readAt: serverTimestamp(),
    })
  })
}

export const dismissHelperNotification = async (uid: string, id: string): Promise<void> => {
  await mutateUserNotifications('CLEAR', [id]).catch(async () => {
    await updateDoc(doc(firestoreDb, 'notifications', uid, 'items', id), {
      dismissedAt: serverTimestamp(),
    })
  })
}

/** Restores Helper drawer entries that were soft-cleared by the user. */
export const restoreHelperNotification = async (uid: string, ids: string[]): Promise<void> => {
  if (!ids.length) return
  await mutateUserNotifications('RESTORE', ids).catch(async () => {
    const batch = writeBatch(firestoreDb)
    for (const id of ids) {
      batch.update(doc(firestoreDb, 'notifications', uid, 'items', id), { dismissedAt: null })
    }
    await batch.commit()
  })
}

export const openHelperNotification = async (uid: string, id: string): Promise<void> => {
  await mutateUserNotifications('CLICK', [id]).catch(async () => {
    await updateDoc(doc(firestoreDb, 'notifications', uid, 'items', id), {
      readAt: serverTimestamp(),
    })
  })
  await dismissHelperNotification(uid, id)
}

export const markAllHelperNotificationsRead = async (uid: string): Promise<void> => {
  await mutateAllUserNotifications('READ').catch(async () => {
    const q = query(collection(firestoreDb, 'notifications', uid, 'items'), limit(HARD_CAP))
    const snapshot = await getDocs(q)
    if (snapshot.empty) return
    const batch = writeBatch(firestoreDb)
    for (const entry of snapshot.docs) {
      const data = entry.data() as Record<string, unknown>
      if (data.readAt) continue
      batch.update(entry.ref, { readAt: serverTimestamp() })
    }
    await batch.commit()
  })
}
